/** Per-``Announcement.category`` visual tokens.
 *
 *  GENERATED FILE — DO NOT EDIT.
 *
 *  Source of truth: ``backend/crm/announcement_visuals.py``.
 *  Regenerate: ``python backend/scripts/generate_announcement_visuals.py``.
 *
 *  The map lives in the backend because surfaces that render server-side
 *  cannot import TypeScript — digital-signage boards are built in Python
 *  and handed to a provider as finished markup (#124). Keeping a second
 *  hand-maintained copy here is exactly the drift #327 removed.
 */

export interface CategoryVisuals {
  /** Background. CSS color string. */
  background: string;
  /** Pulse / accent color (also the kiosk LED hint). */
  accent: string;
  /** Human-readable label rendered above the title. */
  label: string;
  /** Whether the surface should pulse (kiosk LED bar + web animation). */
  pulseLed: boolean;
  /** Emoji shown alongside the label — a glanceable identifier. */
  icon: string;
  /** Mantine colour NAME (not a hex) for surfaces built from Mantine
   *  primitives — `<Badge color>`, `<ThemeIcon color>`, and the corner card's
   *  left border. Kept alongside the hexes so every surface colours from the
   *  SAME axis: see the note on `visualsFor`. */
  mantineColor: string;
}

const UNKNOWN: CategoryVisuals = {
  background: "#475569",
  accent: "#cbd5e1",
  label: "Announcement",
  pulseLed: false,
  icon: "\ud83d\udce3",
  mantineColor: "gray",
};

const CATEGORY_MAP: Record<string, CategoryVisuals> = {
  "emergency.evacuate": {
    background: "#7f1d1d",
    accent: "#fca5a5",
    label: "Evacuate",
    pulseLed: true,
    icon: "\ud83d\udea8",
    mantineColor: "red",
  },
  "emergency.alarm_arming": {
    background: "#92400e",
    accent: "#fcd34d",
    label: "Alarm arming",
    pulseLed: true,
    icon: "\ud83d\udd14",
    mantineColor: "orange",
  },
  "emergency.drill": {
    background: "#155e75",
    accent: "#67e8f9",
    label: "Drill",
    pulseLed: false,
    icon: "\ud83e\uddea",
    mantineColor: "cyan",
  },
  "emergency.custom": {
    background: "#7f1d1d",
    accent: "#fca5a5",
    label: "Emergency",
    pulseLed: true,
    icon: "\u26a0\ufe0f",
    mantineColor: "red",
  },
  "announcement.maintenance": {
    background: "#1e3a8a",
    accent: "#93c5fd",
    label: "Maintenance",
    pulseLed: false,
    icon: "\ud83d\udd27",
    mantineColor: "blue",
  },
  "announcement.event": {
    background: "#365314",
    accent: "#bef264",
    label: "Event",
    pulseLed: false,
    icon: "\ud83d\udce2",
    mantineColor: "lime",
  },
  "announcement.info": {
    background: "#334155",
    accent: "#94a3b8",
    label: "Info",
    pulseLed: false,
    icon: "\u2139\ufe0f",
    mantineColor: "gray",
  },
  "announcement.custom": UNKNOWN,
};

/**
 * THE colour source for announcements, on every surface (#327).
 *
 * Colour, icon and label come from the CATEGORY — never from `severity`.
 * The two palettes already agreed where both could speak (`emergency.evacuate`
 * is severity-critical red `#7f1d1d`; `emergency.alarm_arming` is
 * severity-warning amber `#92400e`), but category expresses distinctions
 * severity structurally cannot — and one of them is a safety property:
 * a DRILL is `emergency.*` and usually flagged critical, so a severity-driven
 * background paints it the same red as a real evacuation. Category paints it
 * cyan. A drill must not look like the real thing.
 *
 * `severity` still governs BEHAVIOUR — the pulse on critical rows, and whether
 * an announcement may be permanently dismissed (see `helpers.ts`). It just does
 * not pick colours.
 */
export function visualsFor(category: string): CategoryVisuals {
  return CATEGORY_MAP[category] ?? UNKNOWN;
}
